import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Central control for how Task detail / Task create surfaces are presented and
 * for the "just visited" row highlight.
 *
 * Two display modes, persisted per user in localStorage:
 *  - "page"   (default / legacy): task detail opens as the /tasks/:id route and
 *             task create opens as a full-screen dialog.
 *  - "drawer" (opt-in): both open as a right-side drawer that covers ~60% of the
 *             viewport, keeping the originating list visible underneath.
 *
 * Whichever mode is active, the task a user opened from a list gets a green
 * border for {@link HIGHLIGHT_MS} after the detail surface closes so they can
 * re-locate it in the list.
 */
const STORAGE_KEY = "task-display-mode";
const HIGHLIGHT_MS = 5000;
const DETAIL_ROUTE_RE = /\/tasks\/[^/]+\/?$/;

const noop = () => {};

const TaskOverlayContext = createContext({
  displayMode: "page",
  setDisplayMode: noop,
  openTaskDetail: noop,
  showTaskAsDrawer: noop,
  closeTaskDetail: noop,
  detailTaskId: null,
  highlightTaskId: null,
});

export const useTaskOverlay = () => useContext(TaskOverlayContext);

/**
 * sx fragment that draws the green "just visited" border without shifting
 * layout. Spread onto a row/card when its task id matches `highlightTaskId`.
 */
export const taskHighlightSx = {
  boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.success.main}`,
  borderRadius: 1,
  transition: "box-shadow 0.3s ease",
};

const readStoredMode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "drawer" ? "drawer" : "page";
  } catch {
    return "page";
  }
};

export const TaskOverlayProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [displayMode, setDisplayModeState] = useState(readStoredMode);
  // Kept in a ref so callbacks read the latest mode without being recreated.
  const displayModeRef = useRef(displayMode);
  displayModeRef.current = displayMode;

  const setDisplayMode = useCallback((mode) => {
    const next = mode === "drawer" ? "drawer" : "page";
    setDisplayModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore storage errors (e.g. private mode) */
    }
  }, []);

  // Drawer-mode detail surface.
  const [detailTaskId, setDetailTaskId] = useState(null);

  // Green "just visited" highlight.
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  const highlightTimer = useRef(null);
  const pendingHighlight = useRef(null);

  const triggerHighlight = useCallback((id) => {
    if (id == null) return;
    setHighlightTaskId(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => {
      setHighlightTaskId(null);
      highlightTimer.current = null;
    }, HIGHLIGHT_MS);
  }, []);

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    [],
  );

  // Page mode: highlight the task once we navigate back off its detail route.
  useEffect(() => {
    if (pendingHighlight.current == null) return;
    if (!DETAIL_ROUTE_RE.test(location.pathname)) {
      triggerHighlight(pendingHighlight.current);
      pendingHighlight.current = null;
    }
  }, [location.pathname, triggerHighlight]);

  const openTaskDetail = useCallback(
    (taskId) => {
      if (taskId == null) return;
      if (displayModeRef.current === "drawer") {
        setDetailTaskId(taskId);
      } else {
        pendingHighlight.current = taskId;
        navigate(`/tasks/${taskId}`);
      }
    },
    [navigate],
  );

  // Open the drawer detail surface for a task regardless of the persisted
  // display mode. Used by the full-page detail route to switch that task back
  // into the drawer presentation without changing the user's saved preference.
  const showTaskAsDrawer = useCallback((taskId) => {
    if (taskId == null) return;
    setDetailTaskId(taskId);
  }, []);

  const closeTaskDetail = useCallback(() => {
    setDetailTaskId((prev) => {
      if (prev != null) triggerHighlight(prev);
      return null;
    });
  }, [triggerHighlight]);

  const value = {
    displayMode,
    setDisplayMode,
    openTaskDetail,
    showTaskAsDrawer,
    closeTaskDetail,
    detailTaskId,
    highlightTaskId,
  };

  return (
    <TaskOverlayContext.Provider value={value}>
      {children}
    </TaskOverlayContext.Provider>
  );
};

export default TaskOverlayContext;
